import { isValidEmail } from '@dealstack/shared/lib/validation';
import {
	BulkInviteAuthorisedSignatoriesPreviewRes,
	BulkInviteAuthorisedSignatoryResultRes,
} from '@dealstack/shared/lib/generated';
import { BulkInviteSkipReason } from '@dealstack/shared/lib/bulkInviteSkipReason';
import type {
	BulkInviteAuthorisedSignatoryConfirmPayload,
	RoleCancellations,
	RoleInput,
} from './BulkInviteAuthorisedSignatoryModal.types';

/**
 * Flattens the modal's per-role invitee inputs and marked cancellations into the wire
 * payload. A role only contributes a change when its invitee input is fully completed
 * and valid; an incomplete row is silently dropped rather than blocking submission of
 * the rest.
 */
export const buildBulkInviteConfirmPayload = (
	preview: BulkInviteAuthorisedSignatoriesPreviewRes,
	inputs: Record<string, RoleInput>,
	cancelledEmailsByRole: RoleCancellations
): BulkInviteAuthorisedSignatoryConfirmPayload => {
	const changes: BulkInviteAuthorisedSignatoryConfirmPayload['changes'] = [];
	const cancellations: BulkInviteAuthorisedSignatoryConfirmPayload['cancellations'] = [];

	for (const role of preview.roles) {
		const input = inputs[role.roleName];
		if (
			input &&
			input.email.trim() &&
			input.firstName.trim() &&
			input.lastName.trim() &&
			isValidEmail(input.email.trim())
		) {
			changes.push({
				roleName: role.roleName,
				roleIds: role.roleIds,
				email: input.email.trim(),
				firstName: input.firstName.trim(),
				lastName: input.lastName.trim(),
			});
		}

		for (const email of cancelledEmailsByRole[role.roleName] ?? []) {
			cancellations.push({ roleName: role.roleName, roleIds: role.roleIds, email });
		}
	}

	return { changes, cancellations };
};

/** How many bulk-invite items were skipped because that email already held the role's other slot. */
export const countDuplicateSignatorySkips = (
	results: BulkInviteAuthorisedSignatoryResultRes[]
): number =>
	results.filter(
		(r) => !r.success && r.reason === BulkInviteSkipReason.DUPLICATE_SIGNATORY_ON_ROLE
	).length;
